import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import '../styles/recipecosts.css';
import '../styles/recipe-suggestions-modern.css';

// Add a constant for the placeholder image
const PLACEHOLDER_IMAGE = 'https://placehold.co/300x200/png?text=No+Image';

export default function RecipeSuggestions({ recipeId, ingredients, ingredientPrices }) {
    const navigate = useNavigate();
    const [suggestedRecipes, setSuggestedRecipes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasInitiallyFetched, setHasInitiallyFetched] = useState(false);

    // Function to handle recipe click with scroll to top
    const handleRecipeClick = (recipeId, e) => {
        e.preventDefault();
        console.log('Navigating to recipe:', recipeId);
        
        // Navigate to the recipe
        navigate(`/recipe/${recipeId}`);
        
        // Scroll to top after navigation
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
    };

    useEffect(() => {
        const fetchSuggestions = async () => {
            // Reset states at the start of each fetch
            setError(null);
            setSuggestedRecipes([]);
            
            if (!ingredients) {
                console.log('Missing required ingredients data');
                return;
            }

            // Calculate excess ingredients only if we have price data
            let excessIngredients = [];
            
            if (ingredientPrices && Object.keys(ingredientPrices).length > 0) {
                excessIngredients = ingredients.map(ingredient => {
                    try {
                        if (!ingredient || !ingredient.name) {
                            console.warn('Invalid ingredient:', ingredient);
                            return null;
                        }

                        const priceData = ingredientPrices[ingredient.name];
                        
                        // Only use selectedBrand from the initial price calculation
                        // Don't fall back to availableBrands to avoid inconsistency
                        const selectedBrand = priceData?.selectedBrand;
                        
                        if (!selectedBrand) {
                            console.log('No selected brand for ingredient:', ingredient.name);
                            return null;
                        }
                        
                        let excess = 0;

                        if (selectedBrand.baseUnit === 'R/kg') {
                            // For R/kg items, calculate excess in the same unit
                            const amountInKg = ingredient.unit.toLowerCase() === 'kg' 
                                ? parseFloat(ingredient.amount)
                                : parseFloat(ingredient.amount) / 1000;
                            
                            // Calculate packages needed and actual amount bought
                            const actualAmount = Math.ceil(amountInKg * 1000) / 1000; // Round up to nearest gram
                            excess = actualAmount - amountInKg;
                        } else if (selectedBrand.unitEquivalent) {
                            // For packaged items
                            const packageSize = parseFloat(selectedBrand.unitEquivalent.amount);
                            const packagesNeeded = Math.ceil(parseFloat(ingredient.amount) / packageSize);
                            const actualAmount = packagesNeeded * packageSize;
                            excess = actualAmount - parseFloat(ingredient.amount);
                        }

                        // Only return ingredients with significant excess
                        if (excess > 0.01) { // More than 1% excess
                            console.log(`Found excess for ${ingredient.name}:`, excess);
                            return {
                                ...ingredient,
                                excess,
                                actualUnit: selectedBrand.baseUnit === 'R/kg' ? 'kg' : ingredient.unit
                            };
                        }
                        return null;
                    } catch (err) {
                        console.error('Error processing ingredient:', {
                            ingredient,
                            error: err.message,
                            stack: err.stack
                        });
                        return null;
                    }
                }).filter(Boolean);
            }

            console.log('Processed excess ingredients:', excessIngredients);

            // If no excess ingredients, use the main recipe ingredients for suggestions
            let ingredientsToUse = excessIngredients;
            if (excessIngredients.length === 0) {
                console.log('No excess ingredients found, using main recipe ingredients for suggestions');
                ingredientsToUse = ingredients.slice(0, 3).map(ing => ({
                    name: ing.name,
                    amount: ing.amount,
                    unit: ing.unit
                }));
            }

            if (ingredientsToUse.length === 0) {
                console.log('No ingredients available for suggestions');
                return;
            }

            try {
                setLoading(true);
                
                // Get userId from localStorage if available
                const userId = window.localStorage.getItem('userID');
                
                const response = await axios.post(
                    `${API_BASE_URL}/recipes/${recipeId}/suggest-recipes`,
                    { 
                        excessIngredients: ingredientsToUse,
                        userId: userId // Include userId for proper filtering
                    },
                    {
                        timeout: 10000, // 10 second timeout
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    }
                );
                
                console.log('Backend response:', response.data);
                
                if (!response.data?.suggestedRecipes) {
                    console.log('No suggestedRecipes in response:', response.data);
                    // Don't show error for empty suggestions - it's normal
                    return;
                }

                if (response.data.suggestedRecipes.length === 0) {
                    console.log('No recipe suggestions found');
                    return;
                }

                console.log('Received suggestions:', response.data.suggestedRecipes);
                setSuggestedRecipes(response.data.suggestedRecipes);
            } catch (error) {
                console.log('Recipe suggestions unavailable:', error.message);
                // Don't show errors for recipe suggestions - they're optional
                // setError('Error finding recipe suggestions');
                setSuggestedRecipes([]);
            } finally {
                setLoading(false);
            }
        };

        // Fetch suggestions when ingredients are available, regardless of price data
        // Only fetch once per recipe to avoid unnecessary API calls
        if (ingredients && ingredients.length > 0 && !hasInitiallyFetched) {
            fetchSuggestions();
            setHasInitiallyFetched(true);
        }
    }, [recipeId, ingredients, hasInitiallyFetched]);
    
    // Reset the fetch flag when recipe changes
    useEffect(() => {
        setHasInitiallyFetched(false);
        setSuggestedRecipes([]);
    }, [recipeId]);

    // Optional: Refetch suggestions when ingredientPrices are updated (for better excess calculation)
    // This is optional and won't block the initial suggestions
    useEffect(() => {
        if (ingredientPrices && Object.keys(ingredientPrices).length > 0 && hasInitiallyFetched) {
            // Silently refetch with better excess data, but don't show loading
            const refetchSuggestions = async () => {
                try {
                    const userId = window.localStorage.getItem('userID');
                    const response = await axios.post(
                        `${API_BASE_URL}/recipes/${recipeId}/suggest-recipes`,
                        { 
                            excessIngredients: ingredients.slice(0, 3).map(ing => ({
                                name: ing.name,
                                amount: ing.amount,
                                unit: ing.unit
                            })),
                            userId: userId
                        },
                        {
                            timeout: 10000,
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        }
                    );
                    
                    if (response.data?.suggestedRecipes && response.data.suggestedRecipes.length > 0) {
                        setSuggestedRecipes(response.data.suggestedRecipes);
                    }
                } catch (error) {
                    // Silently fail - don't disrupt existing suggestions
                    console.log('Background suggestion update failed:', error.message);
                }
            };
            
            // Debounce the refetch to avoid too many calls
            const timeoutId = setTimeout(refetchSuggestions, 2000);
            return () => clearTimeout(timeoutId);
        }
    }, [ingredientPrices, recipeId, ingredients, hasInitiallyFetched]);

    if (loading) {
        return (
            <div className="modern-recipe-suggestions">
                <div className="container">
                    <div className="suggestions-header">
                        <div className="suggestions-badge">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Similar Recipes
                        </div>
                        <h2 className="suggestions-title">
                            Make the most of your ingredients
                        </h2>
                    </div>
                    
                    <div className="suggestions-loading-modern">
                        <div className="loading-spinner">
                            <svg className="animate-spin w-8 h-8" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                        <p className="loading-text">Finding delicious recipes with your ingredients...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Removed error display - recipe suggestions are optional and shouldn't show errors

    if (suggestedRecipes.length === 0) {
        return null;
    }

    // Sort recipes by number of matched ingredients
    const sortedRecipes = [...suggestedRecipes].sort((a, b) => 
        b.matchedIngredients.length - a.matchedIngredients.length
    );

    return (
        <div className="modern-recipe-suggestions">
            <div className="container">
                <div className="suggestions-header">
                    <div className="suggestions-badge">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Similar Recipes
                    </div>
                    <h2 className="suggestions-title">
                        Make the most of your ingredients
                    </h2>
                    <p className="suggestions-subtitle">
                        Based on what you're already buying, here are some delicious recipes you might love
                    </p>
                </div>
                
                <div className="suggestions-grid">
                    {sortedRecipes.map((recipe, index) => (
                        <div 
                            className="modern-suggestion-card"
                            key={index}
                            onClick={(e) => handleRecipeClick(recipe._id, e)}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="card-image-container">
                                <img 
                                    src={recipe.imageUrl || PLACEHOLDER_IMAGE}
                                    alt={recipe.name}
                                    className="card-image"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = PLACEHOLDER_IMAGE;
                                    }}
                                />
                                <div className="image-overlay">
                                    <div className="match-badge">
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        {recipe.matchedIngredients.length} {recipe.matchedIngredients.length === 1 ? 'match' : 'matches'}
                                    </div>
                                </div>
                            </div>
                            
                            <div className="card-content">
                                <div className="card-header">
                                    <h3 className="recipe-title">{recipe.name}</h3>
                                    <div className="recipe-meta">
                                        <div className="recipe-author">
                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                            </svg>
                                            {recipe.createdBy || 'Mob Kitchen'}
                                        </div>
                                        {recipe.cookingTime && (
                                            <div className="recipe-time">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                {recipe.cookingTime} mins
                                            </div>
                                        )}
                                    </div>
                                </div>
                                
                                {recipe.matchedIngredients && recipe.matchedIngredients.length > 0 && (
                                    <div className="matched-ingredients">
                                        <div className="ingredients-label">
                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                            Shared ingredients:
                                        </div>
                                        <div className="ingredients-tags">
                                            {recipe.matchedIngredients.slice(0, 3).map((ingredient, idx) => (
                                                <span key={idx} className="ingredient-tag">
                                                    {ingredient.name || ingredient}
                                                </span>
                                            ))}
                                            {recipe.matchedIngredients.length > 3 && (
                                                <span className="ingredient-tag more">
                                                    +{recipe.matchedIngredients.length - 3} more
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}
                                
                                <div className="card-action">
                                    <span className="view-recipe-text">View Recipe</span>
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                    </svg>
                                 </div>
                             </div>
                         </div>
                     ))}
                </div>
            </div>
        </div>
    );
} 